'use client'

import { InstrumentLabel, Panel } from './ui'

/**
 * Early-running placeholder for DashboardOverview.
 *
 * Shown for the first few seconds of an audit, while too few tools have
 * returned for the unified score to mean anything — it exists so the customer
 * never sees a real-looking 0 / grade-F red during a normal run.
 *
 * Two rules it has to follow:
 *
 * 1. The blocks use `bg-skeleton`, a white ALPHA, not a surface token. On this
 *    surface `--muted` resolves DARKER than `--card`, so the previous
 *    `bg-muted` blocks rendered dark-on-dark and the whole progress screen
 *    looked like an empty black panel.
 * 2. It mirrors the layout that is about to replace it (hero + figures +
 *    category row). The previous version modelled the pre-overhaul dashboard —
 *    a score ring and three cards — so the page visibly re-flowed the moment
 *    real data arrived.
 *
 * The progress readout is real, not a placeholder: it is the only signal the
 * customer has that anything is happening.
 */
export function DashboardSkeleton({
  domain,
  doneCount,
  totalCount,
}: {
  domain: string | null
  doneCount: number
  totalCount: number
}) {
  const progressPercent =
    totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0

  return (
    <div
      className='flex flex-col gap-5'
      role='status'
      aria-live='polite'
      aria-busy='true'
      aria-label={`Auditing ${domain || 'your domain'}. ${doneCount} of ${totalCount} tools complete.`}
    >
      {/* Hero placeholder */}
      <Panel gradientRule>
        <div className='grid grid-cols-1 lg:grid-cols-[minmax(380px,440px)_1fr]'>
          <div className='flex flex-col gap-5 border-b border-hairline px-6 py-7 sm:px-8 lg:border-b-0 lg:border-r'>
            <InstrumentLabel tone='secondary'>
              AI readiness score
            </InstrumentLabel>
            <div className='flex items-end gap-5'>
              <Block className='h-[72px] w-[56px] sm:h-[96px] sm:w-[72px]' />
              <Block className='h-9 w-24' />
            </div>
            <Block className='h-2 w-full' />
            <div className='flex flex-col gap-2'>
              <Block className='h-4 w-3/4' />
              <Block className='h-3 w-full' />
              <Block className='h-3 w-5/6' />
            </div>
          </div>

          <div className='flex flex-col'>
            {/* Live progress — the one genuinely informative element here. */}
            <div className='flex flex-1 flex-col justify-center gap-3 px-6 py-7 sm:px-8'>
              <div className='flex items-baseline justify-between'>
                <InstrumentLabel tone='secondary'>
                  Running {doneCount}/{totalCount} tools
                </InstrumentLabel>
                <span className='font-mono text-xs tabular-nums text-muted-foreground'>
                  {progressPercent}%
                </span>
              </div>
              <div className='h-1.5 w-full bg-surface-inset'>
                <div
                  className='h-full bg-primary transition-all duration-500 ease-out'
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className='text-xs text-instrument'>
                Scoring starts once enough checks have reported.
              </span>
            </div>

            <div className='grid grid-cols-2 border-t border-hairline sm:grid-cols-4'>
              {['Tools run', 'Pass rate', 'Open issues', 'Last audit'].map(
                (label, i) => (
                  <div
                    key={label}
                    className={[
                      'flex flex-col gap-2 px-5 py-3.5',
                      i % 2 === 0 ? 'border-r border-hairline' : '',
                      i < 2 ? 'border-b border-hairline' : '',
                      'sm:border-b-0',
                      i === 3 ? 'sm:border-r-0' : 'sm:border-r',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <InstrumentLabel tone='muted'>{label}</InstrumentLabel>
                    <Block className='h-5 w-12' />
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </Panel>

      {/* Category row placeholder */}
      <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5'>
        {['Crawlability', 'Content', 'Citations', 'Community', 'Trust'].map(
          label => (
            <Panel key={label} as='div' className='flex flex-col gap-4 p-5'>
              <div className='flex items-center justify-between'>
                <InstrumentLabel tone='secondary'>{label}</InstrumentLabel>
                <Block className='h-4 w-6' />
              </div>
              <Block className='h-8 w-14' />
              <div className='flex flex-col gap-2.5'>
                <Block className='h-[3px] w-full' />
                <Block className='h-[3px] w-full' />
                <Block className='h-[3px] w-2/3' />
              </div>
            </Panel>
          )
        )}
      </div>
    </div>
  )
}

/**
 * A single shimmer block. `motion-reduce:animate-none` because a pulsing
 * placeholder is exactly the kind of thing that triggers vestibular
 * discomfort, and the layout reads fine without it.
 */
function Block({ className = '' }: { className?: string }) {
  return (
    <span
      aria-hidden='true'
      className={`block animate-pulse bg-skeleton motion-reduce:animate-none ${className}`}
    />
  )
}
